import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { AdornisRoles } from '@adornis/users/db/a-roles.js';
import '@chancenwerk/client/components/semantics/chancen-component-semantic-kpf-picker.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { Course } from '@chancenwerk/shared/courses/Course.js';
import { COURSE_TITLES } from '@chancenwerk/shared/courses/CourseHelper.js';
import { CourseIntensive } from '@chancenwerk/shared/courses/CourseIntensive.js';
import { CourseLernfoerderung } from '@chancenwerk/shared/courses/CourseLernfoerderung.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import { Role } from '@chancenwerk/shared/semantics/Role.js';
import '@chancenwerk/client/design/components/cr-progress-bar.js';
import { colors } from '@chancenwerk/client/design/theme.js';
import type { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { combineLatest, switchMap } from 'rxjs';
import '../components/board-component-course-digital-card.js';
import { BoardRoutes } from '../routes.js';

const courseSelector: BaseQLSelectionSet<Course | CourseIntensive> = {
  _id: 1,
  _class: 1,
  name: 1,
  kpfID: 1,
  location: 1,
  weekday: 1,
  startTime: 1,
  endTime: 1,
  leKoIDs: 1,
  osStudentIDs: 1,
  usStudentIDs: 1,
  subjectOne: 1,
  subjectTwo: 1,
  schuKoIDs: 1,
  startDate: 1,
  plannedStartDate: 1,
};

const userSelector: BaseQLSelectionSet<ChancenwerkUser> = {
  _id: 1,
  _class: 1,
  vorname: 1,
  nachname: 1,
  email: 1,
  roles: AdornisRoles.allFields,
};

/**
 * @customElement board-page-week-overview
 */
@customElement('board-page-week-overview')
export class BoardPageWeekOverview extends CrPage {
  constructor() {
    super();
    this.label = 'Wochenübersicht Präsenzkurse';
    this.permission = ChancenPermission.board.VIEW_WEEK_OVERVIEW;
  }
  @state() private readonly _baseDate = new RXController(this, DateTime.now());

  @state() private readonly _selectedKpf = new RXController<Maybe<KPF>>(this, null);

  @state() private readonly _coursesFromKPF = new RXController(
    this,
    this._selectedKpf.observable.pipe(
      switchMap(kpf => (kpf ? Course.subscribeCoursesByKPFID([kpf._id])(courseSelector) : [])),
    ),
  );

  @state() private readonly _intensiveCoursesFromKPF = new RXController(
    this,
    this._selectedKpf.observable.pipe(
      switchMap(kpf => (kpf ? Course.subscribeCoursesByKPFID([kpf._id], 'CourseIntensive')(courseSelector) : [])),
    ),
  );

  @state() private readonly _leKoSchuKos = new RXController(
    this,
    combineLatest(this._coursesFromKPF.observable, this._intensiveCoursesFromKPF.observable).pipe(
      switchMap(async courses => {
        const combined = [...(courses[0] ?? []), ...(courses[1] ?? [])];
        const users: Record<string, ChancenwerkUser> = {};
        for (let i = 0; i < combined.length; i++) {
          const course = combined[i];
          if (!course) {
            continue;
          }

          const usersCombined = [...(course.leKoIDs ?? []), ...(course.schuKoIDs ?? [])];
          for (let j = 0; j < usersCombined.length; j++) {
            const id = usersCombined[j];
            if (!id) {
              continue;
            }

            const user = await ChancenwerkUser.getByID<ChancenwerkUser>(id)(userSelector);
            if (user) {
              users[id] = user;
            }
          }
        }
        return users;
      }),
    ),
  );

  override header() {
    return html`
      <chancen-component-semantic-kpf-picker
        flex
        placeholder="KPF auswählen"
        .value=${this._selectedKpf.value}
        @value-picked=${async (e: ValueEvent<Maybe<KPF>>) => {
          this._selectedKpf.next(e.detail.value);
        }}
      ></chancen-component-semantic-kpf-picker>
    `;
  }

  override content() {
    if (!this._selectedKpf.value)
      return html` <cr-card padding="xs">
        <cr-flex space="sm" crossaxis-center>
          <cr-icon ${css({ fontSize: '3.5rem' })}>warning</cr-icon>
          <cr-text>Bitte wähle oben-rechts ein KPF aus!</cr-text>
        </cr-flex>
      </cr-card>`;
    if (this._leKoSchuKos.isLoading || this._coursesFromKPF.isLoading || this._intensiveCoursesFromKPF.isLoading) {
      return html`<cr-flex w100 crossaxis-center><cr-loader></cr-loader></cr-flex>`;
    }

    const workdays = 5;
    const weekStart = this._baseDate.value.startOf('week');

    return html`
      <cr-flex horizontal space-between crossaxis-center padding="xs">
        <cr-button
          mode="fab"
          icon="chevron_left"
          @click=${() => this._baseDate.next(this._baseDate.value.minus({ days: 7 }))}
        ></cr-button>
        <cr-flex space="sm" crossaxis-center>
          <cr-h3>Kalenderwoche ${weekStart.weekNumber}</cr-h3>
          <cr-text>${weekStart.toFormat('dd.MM.yyyy')} - ${weekStart.endOf('week').toFormat('dd.MM.yyyy')}</cr-text>
        </cr-flex>
        <cr-button
          mode="fab"
          icon="chevron_right"
          @click=${() => this._baseDate.next(this._baseDate.value.plus({ days: 7 }))}
        ></cr-button>
      </cr-flex>
      <cr-grid columns="1fr 1fr" space="sm" padding="xs">
        ${repeat(
          new Array(workdays).fill(null),
          key => key,
          (_, i) => {
            return this._dayCard(weekStart.plus({ days: i }));
          },
        )}
      </cr-grid>
    `;
  }

  private _dayCard(day: DateTime) {
    const isToday = day.toFormat('yyyy LLL dd') === DateTime.now().toFormat('yyyy LLL dd');
    const outerCss = isToday ? { border: `5px solid ${this.colors.primary.shade(300)}` } : {};

    const coursesOnDay: Course[] = [
      ...(this._coursesFromKPF.value ?? []),
      ...(this._intensiveCoursesFromKPF.value ?? []),
    ].filter(c => this._isCourseOnWeekday(c, day));

    if (coursesOnDay.length === 0) return nothing;

    return html`<cr-card space="md" ${css(outerCss)}>
      <cr-flex flex space="md">
        <cr-h3 bold>${day.toLocaleString({ weekday: 'long', month: 'long', day: '2-digit' })}</cr-h3>
        ${coursesOnDay.length === 0 ? html`<cr-text>Keine Kurse an diesem Tag</cr-text>` : nothing}
        ${repeat(
          coursesOnDay,
          c => c._id,
          c => this._courseElement(c, day),
        )}
      </cr-flex>
    </cr-card>`;
  }

  private _courseElement(c: Course, day: DateTime) {
    const isStarted = this._isCourseStarted(c, day);
    const style = {};
    const leKoMissing = (c.leKoIDs?.length ?? 0) === 0;
    const schuKoMissing = (c.schuKoIDs?.length ?? 0) === 0;

    return html`
      <cr-flex
        space="md"
        ${css({
          ...style,
          borderLeft: `4px solid ${
            !isStarted || leKoMissing || schuKoMissing ? colors.red : colors.chancen_orange_secondary
          }`,
          padding: '5px 0px 5px 10px',
        })}
      >
        <cr-flex space="sm">
          <cr-flex horizontal space-between>
            <cr-flex horizontal crossaxis-center>
              <cr-text bold>${COURSE_TITLES[c._class]} in Raum ${c.location.length > 0 ? c.location : '?'} </cr-text>
              ${this._leKoSchuKoMissing(leKoMissing, schuKoMissing)}
            </cr-flex>
            <cr-icon
              ${css({ fontSize: '24px', color: this.colors.primary, cursor: 'pointer' })}
              @click=${() =>
                goTo(
                  `${BoardRoutes.attendance}?kpf=${this._selectedKpf.value?._id}&course=${
                    c._id
                  }&date=${day.toISODate()}`,
                )}
            >
              visibility
            </cr-icon>
          </cr-flex>
          <cr-text> ${c.startTime?.toFormat('HH:mm')} - ${c.endTime?.toFormat('HH:mm')} </cr-text>
          ${c instanceof CourseIntensive && (c.subjectOne !== '' || c.subjectTwo !== '')
            ? html` <cr-text> ${c.subjectOne} ${c.subjectTwo} </cr-text> `
            : nothing}
        </cr-flex>

        <cr-flex space="md">
          ${this._leKoSchuKoList(c)}
          ${c._class === CourseLernfoerderung._class
            ? html`
                <cr-flex horizontal space="sm">
                  <cr-text bold ${css({ width: '120px' })}> Kinder: </cr-text>
                  <cr-text> ${c.usStudentIDs.length} </cr-text>
                </cr-flex>
              `
            : nothing}
          <cr-flex horizontal space="sm">
            <cr-text bold ${css({ width: '120px' })}> Jugendliche: </cr-text>
            <cr-text> ${c.osStudentIDs.length} </cr-text>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _leKoSchuKoList(c: Course) {
    return html`${!c.schuKoIDs?.length
      ? html`
          <cr-flex horizontal space="sm">
            <cr-text bold ${css({ width: '120px' })}>SchuKo:</cr-text>
            <cr-text ${css({ color: 'red' })}> ? </cr-text>
          </cr-flex>
        `
      : nothing}
    ${!c.leKoIDs?.length
      ? html`
          <cr-flex horizontal space="sm">
            <cr-text bold ${css({ width: '120px' })}>Leko:</cr-text>
            <cr-text ${css({ color: 'red' })}> ? </cr-text>
          </cr-flex>
        `
      : nothing}
    ${repeat(
      [...(c.leKoIDs ?? []), ...(c.schuKoIDs ?? [])],
      id => id,
      id => {
        const user = this._leKoSchuKos.value?.[id];
        if (!user) {
          return nothing;
        }

        let type = 'LeKo';
        if (user.checkRole(Role.SCHUKO)) type = 'SchuKo';

        return html`
          <cr-flex horizontal space="sm">
            <cr-tooltip><cr-text>${user.email}</cr-text></cr-tooltip>
            <cr-text ${css({ width: '120px' })} bold>${type}:</cr-text>
            <cr-link href=${`${projectConfig.get('RAUM_URL')}edit/person/employee/${user._id}`}>
              ${(user.vorname ?? '').length > 0
                ? html`<cr-text>${user.vorname} ${user.nachname}</cr-text>`
                : html`<cr-text>${user.email}</cr-text>`}
            </cr-link>
          </cr-flex>
        `;
      },
    )}`;
  }

  private _leKoSchuKoMissing(leKoMissing: boolean, schuKoMissing: boolean) {
    return html`${leKoMissing || schuKoMissing
      ? html`<cr-icon ${css({ color: this.colors.tone.error, fontSize: '1.8rem', marginLeft: '10px' })}>
          report_problem
          ${schuKoMissing && leKoMissing
            ? html`<cr-tooltip>
                <cr-text> Kein SchuKo und LeKo zugewiesen! </cr-text>
              </cr-tooltip>`
            : schuKoMissing
              ? html`<cr-tooltip> <cr-text> Kein SchuKo zugewiesen! </cr-text> </cr-tooltip>`
              : leKoMissing
                ? html`<cr-tooltip> <cr-text> Kein LeKo zugewiesen! </cr-text> </cr-tooltip>`
                : nothing}
        </cr-icon>`
      : nothing}`;
  }

  private _isCourseStarted(course: Course, day: DateTime): boolean {
    if (course.startDate) {
      return day.toMillis() >= course.startDate.toMillis();
    } else if (course.plannedStartDate) {
      return day.toMillis() >= course.plannedStartDate.toMillis();
    }
    return false;
  }

  private _isCourseOnWeekday(course: Course, day: DateTime): boolean {
    if (!course.weekday) {
      return false;
    }

    return course.weekday === day.weekdayLong;
  }
}
